import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CartEvents, Exception, PaymentCart, PaymentMethods, PaymentMethodsType } from '@cargos/sprintpay-models';
import { combineLatest, finalize, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { CartBillService, CustomerService, PaymentRequestService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import Swal from 'sweetalert2';
import { PaymentMethodsSelected } from '../shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../shared/services/payment-methods-handler.service';

@Component({
    selector: 'app-payment-details',
    templateUrl: './payment-details.component.html',
    styleUrl: './payment-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class PaymentDetailsComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public deletingCart = false;
    public labelItemsInCart: string;
    public paymentRequests: PaymentCart[] = [];
    public awbs: string[] = [];
    public financeDisclaimer: string = '';
    public SPFCreditLimit: number = 0;
    @Input() labelByPanel: string = '';
    @Input() rates: boolean = false;
    @Input() cartSplitByAwbAndFlight: boolean = false;
    @Input() validateBeforeCartDeletion: boolean = false;
    @Input() aboveThreshold: boolean = false;
    @Output() removePaymentById = new EventEmitter<number>();
    @Output() editPayment = new EventEmitter<PaymentCart>();
    @Output() cartDeleted = new EventEmitter<CartEvents>();

    constructor(
        private cartBillService: CartBillService,
        private paymentRequestService: PaymentRequestService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private currencyPipe: CurrencyPipe,
        private customerService: CustomerService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.subscribeCartItems();
        this.subscribeCart();
        this.subscribeSPFRate();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeCart(): void {
        this.cartBillService
            .getCartPaymentRequests$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (allPaymentRequest) => {
                    this.paymentRequests = allPaymentRequest;
                },
            });
    }

    deleteCart(): void {
        this.deletingCart = true;

        const haveThresholdItems = this.aboveThreshold
            ? this.cartBillService.isCartBelowThreshold()
            : this.cartBillService.isCartAboveThreshold();

        of(haveThresholdItems)
            .pipe(
                switchMap((haveThresholdItems) => {
                    return haveThresholdItems
                        ? this.paymentRequestService
                              .deleteCartLib(this.getIds())
                              .pipe(map(() => CartEvents.paymentRequestRemoved))
                        : this.cartBillService.deleteCart().pipe(map(() => CartEvents.cartDeleted));
                }),
                take(1),
                finalize(() => {
                    this.deletingCart = false;
                })
            )
            .subscribe({
                next: (event: CartEvents) => {
                    this.cartDeleted.emit(event);
                },
                error: (error: Exception) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: error.type,
                    });
                },
            });
    }

    removePayment(paymentId: number): void {
        this.removePaymentById.emit(paymentId);
    }

    editPaymentRequest(payment: PaymentCart): void {
        this.editPayment.emit(payment);
    }

    subscribeCartItems(): void {
        this.getAWBsFromCart(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (awbs) => {
                    this.awbs = awbs;
                },
            });
    }

    getAWBsFromCart(aboveThreshold: boolean): Observable<string[]> {
        return this.cartBillService.getAWBsFromCart$(aboveThreshold);
    }

    getIds(): number[] {
        return this.paymentRequests?.map((paymentCart) => paymentCart.id || 0) || [];
    }

    subscribeSPFRate(): void {
        this.featureRestrictionsService
            .isPaymentMethodValid$()
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap((isPaymentMethodSelectedValid) => {
                    if (!isPaymentMethodSelectedValid) {
                        return of([null, 0, 0] as [PaymentMethodsSelected | null, number, number]);
                    }
                    return combineLatest([
                        this.paymentMethodsHandlerService.getPaymentMethodsSelectedName$(),
                        this.cartBillService.getSPFRateByThreshold(false),
                        this.cartBillService.getSPFRateByThreshold(true),
                        this.customerService.getCustomerAccountSPFCreditLimit$(),
                    ]);
                })
            )
            .subscribe({
                next: ([paymentMethodsSelected, underRate, overRate, SPFCreditLimit]) => {
                    this.SPFCreditLimit = SPFCreditLimit || 0;
                    this.financeDisclaimer = this.getFinanceDisclaimer(
                        this.aboveThreshold,
                        this.aboveThreshold
                            ? paymentMethodsSelected?.paymentMethodSelectedAboveThreshold
                            : paymentMethodsSelected?.paymentMethodSelected,
                        this.aboveThreshold ? overRate : underRate,
                        this.SPFCreditLimit || 0
                    );
                },
            });
    }

    getFinanceDisclaimer(
        isAboveThreshold: boolean,
        paymentMethod: PaymentMethodsType | undefined,
        rate: number | null,
        creditLimit: number
    ): string {
        if (rate && paymentMethod === PaymentMethods.SPRINTPAY_FLEX) {
            return `Eligible payments include a ${rate}% finance fee`;
        }

        if (!isAboveThreshold && paymentMethod === PaymentMethods.SPRINTPAY_FLEX && creditLimit) {
            return `No finance fee for payments up to  ${this.currencyPipe.transform(creditLimit)}`;
        }

        return '';
    }
}
